import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Section, Overline, PrimaryButton, OutlineButton } from "../components/UI";
import { SEO } from "../components/SEO";
import {
  Activity,
  Droplet,
  Users,
  Heart,
  Clock,
  Shield,
  Stethoscope,
  ClipboardList,
  Scissors,
  Sparkles,
  Baby,
  LineChart,
  MessageSquare,
  ChevronDown,
  ArrowUpRight,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  HIGH-RISK PREGNANCY PAGE — Master Implementation Brief v1          */
/* ------------------------------------------------------------------ */

const PAGE_TITLE =
  "High-Risk Pregnancy Care — Dr. Tania Gurdip Singh";
const PAGE_DESC =
  "Specialised care for pregnancies requiring closer monitoring, evidence-based management and compassionate support. Learn what makes a pregnancy high-risk, the conditions Dr. Tania manages, and how early specialist assessment supports the safest plan for you and your baby.";

export default function HighRiskPregnancy() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid="hrp-page">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESC}
        jsonLd={faqJsonLd}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "High-Risk Pregnancy", item: "/high-risk-pregnancy" },
        ]}
      />

      {/* 1 · Hero */}
      <section className="bg-bone" data-testid="hrp-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <Overline>Principal Area of Expertise</Overline>
            <div className="h-px w-12 bg-prestige my-6" />
            <h1
              data-testid="hrp-heading"
              className="font-serif italic text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-navy tracking-tight"
            >
              High-Risk Pregnancy Care
            </h1>
            <p
              data-testid="hrp-subheading"
              className="text-lg md:text-xl text-stone2 mt-6 leading-relaxed max-w-2xl"
            >
              Specialised care for pregnancies requiring closer monitoring,
              evidence-based management and compassionate support.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryButton
                to="/contact?type=appointment"
                testid="hrp-hero-cta-primary"
              >
                Schedule a Consultation
              </PrimaryButton>
              <OutlineButton to="#learn" testid="hrp-hero-cta-secondary">
                Learn About High-Risk Pregnancy
              </OutlineButton>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · What is a High-Risk Pregnancy? */}
      <Section id="learn">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <Overline>Understanding the Term</Overline>
            <div className="h-px w-10 bg-prestige my-5" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              What is a high-risk pregnancy?
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <p className="text-lg text-stone2 leading-relaxed">
              A high-risk pregnancy is one in which the mother, the baby, or
              both may benefit from additional attention during pregnancy,
              birth or the postnatal period. It is a clinical description — not
              a prediction.
            </p>
            <p className="text-lg text-stone2 leading-relaxed mt-5">
              Being categorised as high-risk does not mean something will go
              wrong. It means that closer monitoring, thoughtful planning and
              personalised care can meaningfully improve outcomes. In many
              cases, high-risk pregnancies progress calmly and end with a
              healthy mother and baby.
            </p>
            <p className="text-lg text-stone2 leading-relaxed mt-5">
              The value of specialist care is quiet and cumulative: earlier
              recognition, appropriate investigations at the right time, and
              decisions made with the best available evidence.
            </p>
          </div>
        </div>
      </Section>

      {/* 3 · When is a Pregnancy Considered High-Risk? */}
      <Section className="bg-surfacealt/60">
        <div className="max-w-3xl">
          <Overline>Common Contributing Factors</Overline>
          <div className="h-px w-10 bg-prestige my-5" />
          <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
            When is a pregnancy considered high-risk?
          </h2>
          <p className="text-base text-stone2 mt-5 leading-relaxed">
            The following factors commonly warrant closer monitoring. This list
            is informative rather than exhaustive — the presence of a factor
            does not, on its own, define your pregnancy.
          </p>
        </div>

        <ul
          className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5"
          role="list"
        >
          {RISK_FACTORS.map((r) => (
            <li
              key={r.label}
              data-testid={`risk-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="flex items-start gap-3 bg-white border border-line p-5 min-w-0"
            >
              <r.icon
                size={18}
                strokeWidth={1.5}
                className="text-prestige shrink-0 mt-[3px]"
                aria-hidden="true"
              />
              <span className="text-[14px] text-navy font-medium leading-snug min-w-0">
                {r.label}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      {/* 4 · Conditions Managed */}
      <Section>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <Overline>Conditions Managed</Overline>
            <div className="h-px w-10 bg-prestige my-5" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              Representative conditions Dr. Tania supports.
            </h2>
            <p className="text-base text-stone2 mt-5 leading-relaxed">
              A short summary of each. Every plan is individualised — these
              descriptions are a starting point, not a protocol.
            </p>
          </div>
          <Link
            to="/conditions"
            data-testid="hrp-view-conditions"
            className="text-sm mono uppercase tracking-widest text-navy inline-flex items-center gap-1.5 self-start md:self-end"
          >
            All conditions <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {CONDITIONS.map((c) => (
            <article
              key={c.title}
              data-testid={`condition-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-white border border-line p-7 md:p-8 flex flex-col"
            >
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                {c.tag}
              </div>
              <h3 className="font-serif text-[1.35rem] md:text-2xl text-navy mt-3 leading-tight">
                {c.title}
              </h3>
              <p className="text-[14px] text-stone2 mt-3 leading-relaxed flex-1">
                {c.body}
              </p>
            </article>
          ))}
        </div>
      </Section>

      {/* 5 · How Dr. Tania Approaches HRP Care */}
      <section className="bg-navy text-white" data-testid="hrp-approach">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <Overline className="text-prestige-soft">Clinical Approach</Overline>
            <div className="h-px w-12 bg-prestige my-6" />
            <h2 className="font-serif italic text-3xl md:text-5xl text-white tracking-tight leading-[1.1]">
              How Dr. Tania approaches high-risk pregnancy care.
            </h2>
            <p className="text-white/75 text-lg leading-relaxed mt-6 max-w-2xl">
              A steady, unhurried framework — designed to be transparent with
              patients and grounded in current evidence.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-8">
            {APPROACH.map((a, i) => (
              <div
                key={a.title}
                data-testid={`approach-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="border-t border-white/20 pt-5"
              >
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige-soft">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-xl md:text-2xl text-white mt-2 leading-snug">
                  {a.title}
                </h3>
                <p className="text-[15px] text-white/75 mt-3 leading-relaxed">
                  {a.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6 · Why Early Specialist Care Matters */}
      <Section>
        <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <Overline>Early Specialist Care</Overline>
            <div className="h-px w-10 bg-prestige my-5" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              Why timing matters.
            </h2>
            <p className="text-base text-stone2 mt-5 leading-relaxed">
              Early involvement of a specialist rarely changes the diagnosis —
              it changes the number of options available to shape the safest
              plan.
            </p>
          </div>
          <ul
            className="md:col-span-7 md:pt-4 grid sm:grid-cols-2 gap-x-8 gap-y-8"
            role="list"
          >
            {EARLY_CARE_BENEFITS.map((b) => (
              <li
                key={b.title}
                data-testid={`benefit-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="min-w-0"
              >
                <div className="flex items-center gap-2.5">
                  <b.icon
                    size={18}
                    strokeWidth={1.5}
                    className="text-prestige"
                    aria-hidden="true"
                  />
                  <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
                    {b.tag}
                  </div>
                </div>
                <h3 className="font-serif text-xl text-navy mt-2 leading-snug">
                  {b.title}
                </h3>
                <p className="text-[14px] text-stone2 mt-2 leading-relaxed">
                  {b.body}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* 7 · Frequently Asked Questions */}
      <Section className="bg-surfacealt/60">
        <div className="max-w-3xl">
          <Overline>Frequently Asked Questions</Overline>
          <div className="h-px w-10 bg-prestige my-5" />
          <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
            Answers to common questions.
          </h2>
          <p className="text-base text-stone2 mt-5 leading-relaxed">
            Concise, evidence-based responses. If your question is not covered
            here, please raise it during your consultation.
          </p>
        </div>

        <div className="mt-10 max-w-3xl">
          {FAQS.map((f, i) => (
            <FaqRow key={f.q} idx={i} q={f.q} a={f.a} />
          ))}
        </div>
      </Section>

      {/* 8 · Call to Action */}
      <Section className="bg-navy-deep text-white">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <Overline className="text-prestige-soft">Next Step</Overline>
            <h2 className="font-serif text-3xl md:text-5xl text-white mt-3 tracking-tight leading-tight">
              A calm plan starts with a considered first conversation.
            </h2>
            <p className="text-white/70 mt-5 max-w-xl text-lg leading-relaxed">
              If you have been told your pregnancy is high-risk, early
              specialist assessment can help guide the safest plan for you and
              your baby.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3">
            <Link
              to="/contact?type=appointment"
              data-testid="hrp-cta-schedule"
              className="bg-prestige hover:bg-prestige-soft text-navy rounded-full px-6 py-3 text-sm font-medium"
            >
              Schedule Consultation
            </Link>
            <Link
              to="/contact"
              data-testid="hrp-cta-contact"
              className="border border-white/40 text-white hover:bg-white hover:text-navy transition-colors rounded-full px-6 py-3 text-sm font-medium"
            >
              Contact Clinic
            </Link>
            <Link
              to="/articles"
              data-testid="hrp-cta-articles"
              className="text-white/80 hover:text-white text-sm font-medium inline-flex items-center gap-1.5 px-2 py-3"
            >
              Read patient education <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ Accordion Row                                                  */
/* ------------------------------------------------------------------ */

function FaqRow({ idx, q, a }) {
  const [open, setOpen] = useState(idx === 0);
  const slug = q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 50);
  return (
    <div className="border-t border-line last:border-b" data-testid={`faq-${slug}`}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm"
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-serif text-lg md:text-xl text-navy leading-snug pt-0.5">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={1.5}
          className={`text-prestige shrink-0 mt-1 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="pb-6 pr-8 text-[15px] text-stone2 leading-relaxed max-w-3xl">
          {a}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Content constants                                                  */
/* ------------------------------------------------------------------ */

const RISK_FACTORS = [
  { label: "High blood pressure", icon: Activity },
  { label: "Diabetes (pre-existing or gestational)", icon: Droplet },
  { label: "Thyroid disorders", icon: Sparkles },
  { label: "Twin or multiple pregnancy", icon: Users },
  { label: "Previous pregnancy complications", icon: ClipboardList },
  { label: "Previous caesarean section", icon: Scissors },
  { label: "Recurrent pregnancy loss", icon: Heart },
  { label: "Maternal age considerations", icon: Clock },
  { label: "Autoimmune disorders", icon: Shield },
  { label: "Other medical conditions affecting pregnancy", icon: Stethoscope },
];

const CONDITIONS = [
  {
    tag: "Metabolic",
    title: "Gestational Diabetes",
    body: "Blood-glucose changes that first arise during pregnancy. Well-managed, most women achieve excellent outcomes with a combination of nutrition, monitoring and — when needed — medication.",
  },
  {
    tag: "Cardiovascular",
    title: "Hypertensive Disorders of Pregnancy",
    body: "Includes pregnancy-related high blood pressure. Careful surveillance, timely investigations and clear escalation plans form the core of safe management.",
  },
  {
    tag: "Cardiovascular",
    title: "Pre-eclampsia",
    body: "A specific hypertensive condition requiring close monitoring of the mother's blood pressure, kidney and liver function and fetal wellbeing. Early recognition allows planned, safer delivery.",
  },
  {
    tag: "Obstetric",
    title: "Multiple Pregnancy",
    body: "Twin and higher-order pregnancies require more frequent scans, growth surveillance and coordinated planning of birth. Most twin pregnancies proceed well with structured monitoring.",
  },
  {
    tag: "Obstetric History",
    title: "Previous Preterm Birth",
    body: "A prior preterm delivery is a risk factor — not a certainty. Individualised assessment, preventive strategies and cervical surveillance can meaningfully improve the current pregnancy.",
  },
  {
    tag: "Obstetric History",
    title: "Previous Pregnancy Loss",
    body: "Recurrent pregnancy loss deserves a structured, evidence-based investigation and a compassionate care plan for the pregnancy that follows.",
  },
  {
    tag: "Fetal",
    title: "Fetal Growth Restriction",
    body: "When a baby's growth is slower than expected, additional scans and Doppler studies guide the safest timing and mode of birth.",
  },
  {
    tag: "Placental",
    title: "Placental Disorders",
    body: "Conditions such as placenta praevia and placenta accreta spectrum require specialist planning, imaging and multidisciplinary preparation for a safe delivery.",
  },
];

const APPROACH = [
  {
    title: "Individualised assessment",
    body: "Every plan begins with an unhurried review of medical history, current findings and personal circumstances — not a template.",
  },
  {
    title: "Evidence-based recommendations",
    body: "Guidance drawn from ACOG, RCOG, FIGO and NICE, filtered through experienced clinical judgement.",
  },
  {
    title: "Appropriate investigations",
    body: "The right tests, at the right time — enough to inform decisions, without over-investigation.",
  },
  {
    title: "Close monitoring",
    body: "A defined schedule of reviews, scans and laboratory tests, adjusted as pregnancy progresses.",
  },
  {
    title: "Shared decision making",
    body: "Choices are made with the patient, not for the patient. Information is clear; questions are welcomed.",
  },
  {
    title: "Coordination with other specialists",
    body: "When needed, care is coordinated with endocrinologists, cardiologists, haematologists, neonatologists and other colleagues.",
  },
];

const EARLY_CARE_BENEFITS = [
  {
    tag: "Prevention",
    title: "Reducing preventable risk",
    body: "Some complications can be anticipated and, in some cases, prevented — particularly with preconception or first-trimester input.",
    icon: Shield,
  },
  {
    tag: "Monitoring",
    title: "The right surveillance, at the right time",
    body: "A structured schedule ensures nothing important is missed and unnecessary tests are avoided.",
    icon: LineChart,
  },
  {
    tag: "Planning",
    title: "A considered plan for birth",
    body: "Timing, mode and location of birth are decided calmly, in advance, with clear contingencies.",
    icon: ClipboardList,
  },
  {
    tag: "Maternal wellbeing",
    title: "Supporting the mother",
    body: "Physical and emotional wellbeing are treated as clinically important — because they are.",
    icon: Heart,
  },
  {
    tag: "Fetal wellbeing",
    title: "Supporting the baby",
    body: "Growth, movement and placental function are followed closely, with intervention only when clearly indicated.",
    icon: Baby,
  },
  {
    tag: "Communication",
    title: "One clear point of contact",
    body: "A consistent specialist relationship reduces fragmentation and keeps decisions coherent through the pregnancy.",
    icon: MessageSquare,
  },
];

const FAQS = [
  {
    q: "Does a high-risk pregnancy always mean complications?",
    a: "No. High-risk is a description of the level of monitoring that is likely to be helpful — not a prediction of outcome. Many high-risk pregnancies proceed smoothly and end with a healthy mother and baby. The label is a prompt for care, not a warning.",
  },
  {
    q: "Can I still have a normal delivery?",
    a: "Often, yes. The mode of birth is decided individually, based on the specific condition, the progress of the pregnancy and your preferences. Vaginal birth remains possible in many high-risk pregnancies. Caesarean delivery is recommended only when it is clearly the safer choice.",
  },
  {
    q: "How often will I need appointments?",
    a: "Review frequency is tailored to your situation. Some pregnancies need only slightly closer monitoring than a routine pregnancy; others benefit from more frequent visits, especially in the third trimester. A clear schedule is shared with you at the start.",
  },
  {
    q: "Will I need additional scans?",
    a: "Usually yes — but not many. Additional ultrasound assessments (for growth, Doppler studies or fluid measurement) are used when they meaningfully change management. Every extra scan is justified, and never routine for its own sake.",
  },
  {
    q: "Can I continue working?",
    a: "For most high-risk pregnancies, work continues as normal. Any recommendation to modify or step back from work is discussed individually, based on the condition, your role and how the pregnancy is progressing.",
  },
  {
    q: "When should I seek urgent medical advice?",
    a: "Contact your maternity provider or nearest emergency service promptly if you experience heavy bleeding, severe or sudden headache, blurred vision, upper abdominal pain, significant swelling of the face or hands, reduced or absent fetal movements, leaking of fluid, or any symptom that feels wrong to you. When in doubt, reach out — early assessment is always appropriate.",
  },
];
